import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import { getMembershipGroups, setMembershipGroupActive } from "../../utils/membershipApi";
import { getMerchantListing } from "../../utils/apiCalls";
import { successToast } from "../../shared/Toast";
import { formatCap } from "./membershipConstants";

/**
 * Company groups for one merchant.
 *
 * A group is the organisation that bought the memberships. It carries a SHARED spend
 * budget and names one member as the account handler, who gets a read-only view of the
 * team in the pickup app — a company's only window into what it bought, since the app has
 * no purchase flow.
 *
 * Two things this screen has to keep straight, because both are easy to get wrong:
 *
 *   - A group is NOT a tier. One company can span several tiers, so nothing here shows a
 *     discount percentage. The discount belongs to the member's tier.
 *   - An absent budget means NO limit, never zero. `formatCap` renders that as "No limit"
 *     rather than "0", because "0" says the company can never be discounted.
 *
 * As with tiers, there is no delete — retiring a group is a deactivation. The usage ledger
 * attributes historical spend group by group, and its foreign key is RESTRICT, so a delete
 * button could only ever fail.
 */

const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 6,
};

const smallButton = {
    padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
    display: "inline-flex", alignItems: "center", gap: 6,
};

const inputStyle = {
    padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, minWidth: 150,
};

const sar = (value) => `SAR ${Number(value || 0).toFixed(2)}`;

/**
 * How much of the monthly budget is gone.
 *
 * Returns null when there is no budget — a progress figure against "no limit" is
 * meaningless, and rendering it as 0% or 100% would both be wrong.
 */
const monthlyUse = (group) => {
    const cap = group?.maxMonthlyDiscountValue;
    if (cap === null || cap === undefined || Number(cap) <= 0) return null;
    return Math.min(100, Math.round((Number(group.spentThisMonth || 0) / Number(cap)) * 100));
};

const MembershipGroupList = () => {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [groups, setGroups] = useState([]);
    const [merchantList, setMerchantList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [search, setSearch] = useState("");
    const [showRetired, setShowRetired] = useState(false);

    // The API refuses an unscoped list — a budget is commercial terms, so listing every
    // merchant's would leak them. Resolve the merchant first and say so in the UI rather
    // than firing a request that can only 400.
    const merchantFilter = userType === "merchant"
        ? merchantID
        : (selectedMerchant && selectedMerchant !== "all") ? selectedMerchant : null;

    const fetchData = useCallback(async () => {
        if (!merchantFilter) { setGroups([]); return; }
        setIsLoading(true);
        setGroups(await getMembershipGroups({
            merchantId: merchantFilter,
            search: search || undefined,
            isActive: showRetired ? undefined : "true",
        }) || []);
        setIsLoading(false);
    }, [merchantFilter, search, showRetired]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (userType !== "merchant") {
            (async () => setMerchantList(await getMerchantListing()))();
        }
    }, [userType]);

    const tiles = useMemo(() => ({
        total: groups.length,
        members: groups.reduce((sum, g) => sum + (Number(g.members) || 0), 0),
        budgeted: groups.filter((g) =>
            g.maxDailyDiscountValue != null || g.maxMonthlyDiscountValue != null
        ).length,
        // Net of returns, because the ledger stores reversals as negative rows.
        spent: groups.reduce((sum, g) => sum + (Number(g.spentThisMonth) || 0), 0),
    }), [groups]);

    const toggleActive = async (group) => {
        const next = !group.isActive;
        const warning = next
            ? `Reactivate "${group.name}"? Its shared budget starts applying again.`
            : `Deactivate "${group.name}"? Its shared budget stops applying, so its ${group.members} member(s) fall back to their own tier limits — they do NOT stop receiving discounts. Nothing is deleted: past orders and the usage ledger keep their history, and you can reactivate it later.`;
        if (!window.confirm(warning)) return;

        setBusyId(group.id);
        const updated = await setMembershipGroupActive(group.id, next);
        setBusyId(null);
        if (updated) {
            successToast(next ? "Group activated" : "Group deactivated");
            fetchData();
        }
    };

    const merchantNameById = (id) => merchantList.find((m) => m.id === id)?.name || "—";

    return (
        <Fragment>
            <Breadcrumb title="Companies" parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Companies" value={tiles.total} sub={showRetired ? "Including retired" : "Active only"} icon="🏢" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Members" value={tiles.members} sub="Across all companies" icon="👥" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                        <Tile label="With A Budget" value={tiles.budgeted} sub="Daily or monthly cap" icon="🛡️" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={160} />
                        <Tile label="Spent This Month" value={sar(tiles.spent)} sub="Net of returns" icon="💸" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={240} />
                    </div>

                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: 10 }}>
                                <span>🏢 Companies <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {tiles.total} shown</span></span>
                                {merchantFilter && <Link to="/membership/companies/add" style={buttonStyle}>+ Create Company</Link>}
                            </div>
                        }
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                            <input
                                style={{ ...inputStyle, minWidth: 240 }}
                                placeholder="Company name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.textMed }}>
                                <input
                                    type="checkbox"
                                    checked={showRetired}
                                    onChange={(e) => setShowRetired(e.target.checked)}
                                />
                                Show retired
                            </label>
                        </div>

                        {!merchantFilter ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Select a merchant to see its companies.
                            </div>
                        ) : isLoading ? (
                            <Loader />
                        ) : groups.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                {search
                                    ? "No company matches that name."
                                    : <>No companies yet. Click <strong>+ Create Company</strong> to add one — or leave members without a company, which is the normal case for individuals.</>}
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Company</th>
                                            <th style={tbl.th}>Members</th>
                                            <th style={tbl.th}>Handler</th>
                                            <th style={tbl.th}>Daily Budget</th>
                                            <th style={tbl.th}>Monthly Budget</th>
                                            <th style={tbl.th}>Spent This Month</th>
                                            {userType !== "merchant" && <th style={tbl.th}>Merchant</th>}
                                            <th style={tbl.th}>Status</th>
                                            <th style={tbl.th}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groups.map((g) => {
                                            const used = monthlyUse(g);
                                            return (
                                                <tr key={g.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>
                                                        {g.name || "—"}
                                                        {g.arabicName && (
                                                            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400 }}>{g.arabicName}</div>
                                                        )}
                                                    </td>
                                                    <td style={tbl.td}>
                                                        {g.members ?? 0}
                                                        {Number(g.members) !== Number(g.activeMembers) && (
                                                            <div style={{ fontSize: 11, color: C.textLight }}>{g.activeMembers ?? 0} active</div>
                                                        )}
                                                    </td>
                                                    <td style={tbl.td}>
                                                        {/* Nobody nominated is a real, valid state — a company can exist
                                                            before anyone is appointed. It just means no team view. */}
                                                        {g.handlerMembershipId
                                                            ? <Badge color={C.green} bg={C.greenLight}>Appointed</Badge>
                                                            : <span style={{ fontSize: 12, color: C.textLight }}>None yet</span>}
                                                    </td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                        {formatCap(g.maxDailyDiscountValue, " SAR")}
                                                    </td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                        {formatCap(g.maxMonthlyDiscountValue, " SAR")}
                                                    </td>
                                                    <td style={{ ...tbl.td, fontWeight: 600 }}>
                                                        {sar(g.spentThisMonth)}
                                                        {used !== null && (
                                                            <div style={{ fontSize: 11, color: used >= 90 ? C.coral : C.textLight }}>
                                                                {used}% of budget
                                                            </div>
                                                        )}
                                                    </td>
                                                    {userType !== "merchant" && (
                                                        <td style={{ ...tbl.td, color: C.textMed }}>{merchantNameById(g.merchantId)}</td>
                                                    )}
                                                    <td style={tbl.td}>
                                                        {g.isActive
                                                            ? <Badge color={C.green} bg={C.greenLight}>● Active</Badge>
                                                            : <Badge color={C.coral} bg={C.coralLight}>● Retired</Badge>}
                                                    </td>
                                                    <td style={tbl.td}>
                                                        <div style={{ display: "flex", gap: 6 }}>
                                                            <button
                                                                onClick={() => navigate(`/membership/companies/edit/${g.id}`)}
                                                                style={smallButton}
                                                            >
                                                                ✏️ Open
                                                            </button>
                                                            <button
                                                                onClick={() => toggleActive(g)}
                                                                disabled={busyId === g.id}
                                                                style={{
                                                                    ...smallButton,
                                                                    color: g.isActive ? C.coral : C.green,
                                                                    opacity: busyId === g.id ? 0.5 : 1,
                                                                }}
                                                            >
                                                                {g.isActive ? "⏸ Retire" : "▶ Reactivate"}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div style={{ marginTop: 14, fontSize: 12, color: C.textMed }}>
                            A company's budget is <strong>shared</strong>: every member spends from the same pot,
                            counted in the merchant's timezone and net of returns. It does not decide the
                            discount — that comes from each member's tier, and one company can span several.
                        </div>
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

export default MembershipGroupList;
